import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InventoryLayout from './InventoryLayout';

const emptyForm = {
  name: '',
  sku: '',
  barcode: '',
  category: '',
  unit: 'stk',
  reorder_url: '',
  default_partner_id: '',
  default_price: '',
  stock_qty: '',
  min_qty: '',
  location: '',
  active: true
};

const InventoryItemForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyForm);
  const [partnerPrices, setPartnerPrices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [barcodeScanActive, setBarcodeScanActive] = useState(false);
  const [barcodeScanError, setBarcodeScanError] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const detectorRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const res = await fetch('/api/b2b/partners', { headers });
        const data = await res.json();
        if (res.ok) setPartners(Array.isArray(data) ? data : []);
      } catch (_) {
        setPartners([]);
      }
    };
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadItem = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetch(`/api/inventory/items/${id}`, { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente vare');
        setFormData({
          name: data.item.name || '',
          sku: data.item.sku || '',
          barcode: data.item.barcode || '',
          category: data.item.category || '',
          unit: data.item.unit || 'stk',
          reorder_url: data.item.reorder_url || '',
          default_partner_id: data.item.default_partner_id || '',
          default_price: data.item.default_price || '',
          stock_qty: data.item.stock_qty || 0,
          min_qty: data.item.min_qty || 0,
          location: data.item.location || '',
          active: Boolean(data.item.active)
        });
        setPartnerPrices(
          Array.isArray(data.partner_prices)
            ? data.partner_prices.map((entry) => ({
                partner_id: entry.partner_id,
                price: entry.price
              }))
            : []
        );
      } catch (err) {
        setError(err.message || 'Kunne ikke hente vare');
      } finally {
        setLoading(false);
      }
    };
    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!barcodeScanActive) return undefined;
    let cancelled = false;
    const startScan = async () => {
      if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setBarcodeScanError('Kamera er ikke tilgængeligt på denne enhed.');
        setBarcodeScanActive(false);
        return;
      }
      if (!('BarcodeDetector' in window)) {
        setBarcodeScanError('Browseren understøtter ikke stregkodescanning.');
        setBarcodeScanActive(false);
        return;
      }
      try {
        setBarcodeScanError('');
        detectorRef.current = new window.BarcodeDetector({ formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_e', 'upc_a'] });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        scanIntervalRef.current = window.setInterval(async () => {
          if (!videoRef.current || !detectorRef.current) return;
          try {
            const barcodes = await detectorRef.current.detect(videoRef.current);
            if (barcodes.length > 0) {
              const value = barcodes[0]?.rawValue || '';
              if (value) {
                setFormData((prev) => ({ ...prev, barcode: value }));
                setBarcodeScanActive(false);
              }
            }
          } catch (_) {
            // ignore scan errors
          }
        }, 400);
      } catch (err) {
        setBarcodeScanError('Kunne ikke starte kameraet.');
        setBarcodeScanActive(false);
      }
    };
    startScan();

    return () => {
      cancelled = true;
      if (scanIntervalRef.current) {
        window.clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, [barcodeScanActive]);

  useEffect(() => {
    if (!barcodeScanActive && scanIntervalRef.current) {
      window.clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (!barcodeScanActive && streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, [barcodeScanActive]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...formData,
        default_partner_id: formData.default_partner_id || null,
        default_price: Number(formData.default_price || 0),
        stock_qty: Number(formData.stock_qty || 0),
        min_qty: Number(formData.min_qty || 0),
        active: formData.active ? 1 : 0,
        partner_prices: partnerPrices
      };
      const res = await fetch(id ? `/api/inventory/items/${id}` : '/api/inventory/items', {
        method: id ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke gemme vare');
      navigate('/lager/varer');
    } catch (err) {
      setError(err.message || 'Kunne ikke gemme vare');
    } finally {
      setSaving(false);
    }
  };

  const updatePartnerPrice = (index, field, value) => {
    setPartnerPrices((prev) => prev.map((entry, idx) => (idx === index ? { ...entry, [field]: value } : entry)));
  };

  const addPartnerPrice = () => {
    setPartnerPrices((prev) => [...prev, { partner_id: '', price: '' }]);
  };

  const removePartnerPrice = (index) => {
    setPartnerPrices((prev) => prev.filter((_, idx) => idx !== index));
  };

  return (
    <InventoryLayout>
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          {id ? 'Rediger vare' : 'Ny vare'}
        </h2>
      </div>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      {loading ? (
        <div className="text-gray-500">Indlæser...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Navn *</label>
              <input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Kategori</label>
              <input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Varenummer</label>
              <input
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Stregkode (EAN)</label>
              <input
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <button
                  type="button"
                  onClick={() => setBarcodeScanActive((prev) => !prev)}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm"
                >
                  {barcodeScanActive ? 'Stop scanning' : 'Scan stregkode'}
                </button>
                {barcodeScanError && <span className="text-xs text-red-600">{barcodeScanError}</span>}
              </div>
              {barcodeScanActive && (
                <div className="mt-3">
                  <video ref={videoRef} className="w-full rounded-md border border-gray-200 dark:border-gray-700" />
                  <p className="text-xs text-gray-500 mt-1">Peg kameraet på stregkoden.</p>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Enhed</label>
              <input
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Genbestil URL</label>
              <input
                type="url"
                value={formData.reorder_url}
                onChange={(e) => setFormData({ ...formData, reorder_url: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
                placeholder="https://"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Standard leverandør</label>
              <select
                value={formData.default_partner_id}
                onChange={(e) => setFormData({ ...formData, default_partner_id: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              >
                <option value="">Ingen</option>
                {partners.map((partner) => (
                  <option key={partner.id} value={partner.id}>{partner.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Standard pris</label>
              <input
                type="number"
                step="0.01"
                value={formData.default_price}
                onChange={(e) => setFormData({ ...formData, default_price: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Lagerantal</label>
              <input
                type="number"
                value={formData.stock_qty}
                onChange={(e) => setFormData({ ...formData, stock_qty: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Min antal</label>
              <input
                type="number"
                value={formData.min_qty}
                onChange={(e) => setFormData({ ...formData, min_qty: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Lokation</label>
              <input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="mt-1 w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="active"
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
              <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-200">Aktiv</label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">Partner-specifikke priser</h3>
              <button
                type="button"
                onClick={addPartnerPrice}
                className="text-sm text-falck-blue hover:underline"
              >
                Tilføj pris
              </button>
            </div>
            {partnerPrices.length === 0 ? (
              <p className="text-sm text-gray-500">Ingen partnerpriser endnu.</p>
            ) : (
              <div className="space-y-2">
                {partnerPrices.map((entry, index) => (
                  <div key={`${entry.partner_id}-${index}`} className="flex flex-col md:flex-row gap-2">
                    <select
                      value={entry.partner_id}
                      onChange={(e) => updatePartnerPrice(index, 'partner_id', e.target.value)}
                      className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 flex-1"
                    >
                      <option value="">Vælg partner</option>
                      {partners.map((partner) => (
                        <option key={partner.id} value={partner.id}>{partner.name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      value={entry.price}
                      onChange={(e) => updatePartnerPrice(index, 'price', e.target.value)}
                      className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 md:w-40"
                      placeholder="Pris"
                    />
                    <button
                      type="button"
                      onClick={() => removePartnerPrice(index)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Fjern
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate('/lager/varer')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
            >
              Annuller
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-falck-blue text-white rounded-md disabled:opacity-60"
            >
              {saving ? 'Gemmer...' : 'Gem'}
            </button>
          </div>
        </form>
      )}
    </InventoryLayout>
  );
};

export default InventoryItemForm;
